import { useEffect, useState } from 'react';
import { ArrowRight, FileText, ImagePlus } from 'lucide-react';
import { toast } from 'sonner';
import RichText from '../../components/RichText';
import { settingsAPI } from '../../lib/api';
import { t } from '../../lib/i18n';
import { Field, Modal, inputCls } from '../../components/ui';
import ImageAdjust from '../../components/ImageAdjust';
import { refreshBrand, useBrand } from '../../hooks/useBrand';
import { Badge, PageHeader, adminBtnOutline, adminBtnPrimary, adminCard, adminInput } from '../../components/adminUi';

// Admin 8 · Settings (s134 redesign): sub-nav (Home banner · Footer pages); the
// banner edits against a live preview of the storefront hero; footer pages are
// a list (status + length warning) that opens each page's editor in a modal.

// SVGs aren't canvas-friendly — they upload raw (adjust modal skipped).
const isSvg = (file) => file && (file.type === 'image/svg+xml' || /\.svg$/i.test(file.name || ''));

const PAGE_KEYS = ['about', 'terms', 'privacy', 'cookie'];
const PAGE_LABELS = { about: 'About', terms: 'Terms of use', privacy: 'Privacy policy', cookie: 'Cookie policy' };
const SHORT_TEXT = 200; // below this a policy page is almost certainly a placeholder

const textLength = (html) => (html || '').replace(/<[^>]*>/g, ' ').replace(/&nbsp;/g, ' ').replace(/\s+/g, ' ').trim().length;

export default function SettingsPage() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [adjustFile, setAdjustFile] = useState(null);
  const [section, setSection] = useState('banner');
  const [editing, setEditing] = useState(null); // page key open in the modal
  const brand = useBrand();
  const [savedWordmark, setSavedWordmark] = useState(null);

  useEffect(() => {
    settingsAPI.get().then(({ data }) => { setCfg(data); setSavedWordmark((data.branding || {}).wordmark || ''); }).catch(() => toast.error(t('Could not load settings')));
  }, []);

  useEffect(() => {
    if (!dirty) return undefined;
    const warn = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  const setBanner = (patch) => {
    setCfg((prev) => ({ ...prev, banner: { ...prev.banner, ...patch } }));
    setDirty(true);
  };
  const setPage = (key, patch) => {
    setCfg((prev) => ({ ...prev, pages: { ...prev.pages, [key]: { ...(prev.pages?.[key] || {}), ...patch } } }));
    setDirty(true);
  };

  const setBranding = (patch) => {
    setCfg((prev) => ({ ...prev, branding: { ...(prev.branding || {}), ...patch } }));
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await settingsAPI.put(cfg);
      setCfg(data);
      setDirty(false);
      const wm = (data.branding || {}).wordmark || '';
      if (wm !== savedWordmark) {
        setSavedWordmark(wm);
        refreshBrand(); // header/sidebar of every open shell repaint with the new name
      }
      toast.success(t('Saved'));
    } catch (err) {
      toast.error(err.response?.data?.detail || t('Save failed'));
    } finally {
      setSaving(false);
    }
  };

  const uploadBanner = async (file) => {
    try {
      const { data } = await settingsAPI.uploadBanner(file);
      setBanner({ image: data.url });
      toast.success(t('Image uploaded — remember to Save.'));
    } catch (err) {
      toast.error(err.response?.data?.detail || t('Upload failed'));
    }
  };
  const pickBanner = (e) => {
    const f = e.target.files[0];
    if (f) { if (isSvg(f)) uploadBanner(f); else setAdjustFile(f); }
    e.target.value = '';
  };

  if (!cfg) return <p className="text-sm text-lms-muted">{t('Loading…')}</p>;
  const banner = cfg.banner || {};
  const wordmark = (cfg.branding || {}).wordmark || '';
  const defaultWordmark = brand.default_wordmark || 'LMS';

  const navBtn = (key, label) => (
    <button
      key={key}
      type="button"
      onClick={() => setSection(key)}
      aria-current={section === key ? 'page' : undefined}
      className={`rounded-lg px-3 py-2.5 text-left text-sm ${section === key ? 'bg-lms-current font-bold text-lms-ink' : 'font-medium text-lms-body hover:bg-lms-raised'}`}
    >
      {label}
    </button>
  );

  return (
    <div className="flex flex-col gap-[22px]">
      <PageHeader title={t('Settings')} subtitle={t('What students see on the LMS storefront.')}>
        {dirty && <span className="text-[13px] font-semibold text-lms-tag-alt-ink">{t('Unsaved changes')}</span>}
        <button type="button" onClick={save} disabled={saving || !dirty} className={adminBtnPrimary}>
          {saving ? t('Saving…') : t('Save changes')}
        </button>
      </PageHeader>

      <div className="grid items-start gap-6 lg:grid-cols-[220px,minmax(0,1fr)]">
        <nav aria-label={t('Settings')} className={`${adminCard} flex flex-col gap-0.5 p-2.5`}>
          <span className="px-2.5 pb-2 pt-1.5 text-[11px] font-bold uppercase tracking-[0.08em] text-lms-faint">{t('Storefront')}</span>
          {navBtn('branding', t('Branding'))}
          {navBtn('banner', t('Home banner'))}
          {navBtn('pages', t('Footer pages'))}
        </nav>

        <div className="flex max-w-[900px] flex-col gap-5">
          {section === 'branding' && (
            <section className={`${adminCard} flex flex-col gap-4 p-5 sm:px-6`}>
              <div className="flex items-baseline justify-between">
                <h2 className="text-base font-bold text-lms-ink">{t('Branding')}</h2>
                <span className="text-[12.5px] text-lms-sub">{t('Live preview')}</span>
              </div>
              {/* Same lockup as components/Brand.js on the navy sidebar. */}
              <div className="flex items-center gap-2.5 self-start rounded-xl bg-lms-sidebar px-5 py-4 text-white">
                {brand.logo ? <img src={brand.logo} alt="" className="h-7 w-auto object-contain" /> : null}
                {wordmark.trim()
                  ? <span className="text-base font-semibold tracking-tight">{wordmark.trim()}</span>
                  : !brand.logo && <span className="text-sm text-white/60">{t('(logo only — no logo set in the CMS)')}</span>}
              </div>
              <label className="flex flex-col gap-1.5">
                <span className="text-[13px] font-semibold text-lms-body">{t('Name shown next to the logo')}</span>
                <input
                  value={wordmark}
                  onChange={(e) => setBranding({ wordmark: e.target.value.slice(0, 60) })}
                  placeholder={`${t('e.g.')} ${defaultWordmark}`}
                  maxLength={60}
                  className={adminInput}
                />
                <span className="text-xs text-lms-faint">
                  {t('Used in the sidebar, the lesson player and the browser tab. Leave empty to show only the logo.')}
                </span>
              </label>
              <p className="rounded-[10px] bg-lms-raised px-4 py-3 text-[13px] leading-relaxed text-lms-sub">
                {t('The logo comes from the main CMS: Settings → the logo for dark backgrounds.')}
              </p>
            </section>
          )}

          {section === 'banner' && (
            <section className={`${adminCard} flex flex-col gap-4 p-5 sm:px-6`}>
              <div className="flex items-baseline justify-between">
                <h2 className="text-base font-bold text-lms-ink">{t('Home banner')}</h2>
                <span className="text-[12.5px] text-lms-sub">{t('Live preview')}</span>
              </div>
              {/* Same composition as the storefront hero (pages/Home.js). */}
              <div
                className="flex min-h-[132px] flex-wrap items-center justify-between gap-5 rounded-2xl px-7 py-6"
                style={{
                  backgroundColor: 'var(--lms-sidebar)',
                  backgroundImage: banner.image
                    ? `linear-gradient(90deg, color-mix(in srgb, var(--lms-sidebar) 94%, transparent), color-mix(in srgb, var(--lms-sidebar) 80%, transparent)), url(${banner.image})`
                    : undefined,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                }}
              >
                <div className="flex max-w-xl flex-col gap-1.5">
                  <span className="text-2xl font-bold leading-tight text-white">{banner.title || t('Learn at your own pace.')}</span>
                  <span className="text-sm text-lms-band-sub">{banner.subtitle || t('Courses built by the people who use them every day.')}</span>
                </div>
                <span className="inline-flex h-10 items-center gap-2 rounded-[10px] bg-lms-accent px-[18px] text-[13.5px] font-bold text-lms-accent-ink">
                  {t('Browse courses')} <ArrowRight size={14} />
                </span>
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <label className="flex flex-col gap-1.5">
                  <span className="text-[13px] font-semibold text-lms-body">{t('Title')}</span>
                  <input value={banner.title || ''} onChange={(e) => setBanner({ title: e.target.value })} className={adminInput} />
                </label>
                <label className="flex flex-col gap-1.5">
                  <span className="text-[13px] font-semibold text-lms-body">{t('Subtitle')}</span>
                  <input value={banner.subtitle || ''} onChange={(e) => setBanner({ subtitle: e.target.value })} className={adminInput} />
                </label>
              </div>
              <div className="flex flex-col gap-1.5">
                <span className="text-[13px] font-semibold text-lms-body">{t('Background image')}</span>
                <div className="flex flex-wrap items-center gap-3.5 rounded-[10px] border border-lms-line p-2.5">
                  {banner.image ? (
                    <img src={banner.image} alt="" className="h-14 w-[120px] rounded-lg object-cover" />
                  ) : (
                    <span className="flex h-14 w-[120px] items-center justify-center rounded-lg bg-lms-raised text-xs text-lms-faint">{t('No image')}</span>
                  )}
                  <div className="flex min-w-0 flex-1 flex-col">
                    <span className="truncate text-sm text-lms-ink">{banner.image ? banner.image.split('/').pop() : t('Solid brand colour')}</span>
                    <span className="text-xs text-lms-faint">{t('Wide image · 1600 × 400 or larger')}</span>
                  </div>
                  <label className={`${adminBtnOutline} h-10 cursor-pointer`}>
                    <ImagePlus size={15} /> {banner.image ? t('Replace') : t('Upload')}
                    <input type="file" accept="image/*" className="hidden" onChange={pickBanner} />
                  </label>
                  {banner.image && (
                    <button type="button" onClick={() => setBanner({ image: '' })} className="h-10 px-3 text-[13.5px] font-bold text-lms-sale hover:underline">
                      {t('Remove')}
                    </button>
                  )}
                </div>
              </div>
              {adjustFile && (
                <ImageAdjust file={adjustFile} targetRatio={16 / 9} onCancel={() => setAdjustFile(null)}
                  onConfirm={(blobOrFile, filename) => { setAdjustFile(null); const payload = (blobOrFile instanceof File) ? blobOrFile : new File([blobOrFile], filename, { type: blobOrFile.type }); uploadBanner(payload); }} />
              )}
            </section>
          )}

          {section === 'pages' && (
            <section className={`${adminCard} flex flex-col gap-1 p-5 sm:px-6`}>
              <div className="flex items-baseline justify-between pb-2.5">
                <h2 className="text-base font-bold text-lms-ink">{t('Footer pages')}</h2>
                <span className="text-[12.5px] text-lms-sub">{t('Empty pages are hidden from the footer')}</span>
              </div>
              {PAGE_KEYS.map((key) => {
                const pg = cfg.pages?.[key] || {};
                const len = textLength(pg.html);
                return (
                  <div key={key} className="flex items-center gap-3.5 border-t border-lms-line px-1 py-3.5">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] bg-lms-current text-lms-sidebar">
                      <FileText size={16} />
                    </span>
                    <div className="flex min-w-0 flex-1 flex-col">
                      <span className="text-[14.5px] font-bold text-lms-ink">{pg.title || t(PAGE_LABELS[key])}</span>
                      {len === 0 ? (
                        <span className="text-[12.5px] text-lms-sub">{t('No content')}</span>
                      ) : len < SHORT_TEXT ? (
                        <span className="text-[12.5px] text-lms-tag-alt-ink">{t('Very short — add the full text')}</span>
                      ) : (
                        <span className="text-[12.5px] text-lms-sub lms-num">{len} {t('characters')}</span>
                      )}
                    </div>
                    {len > 0 ? <Badge kind="ok">{t('In footer')}</Badge> : <Badge kind="neutral">{t('Hidden')}</Badge>}
                    <button type="button" onClick={() => setEditing(key)} className={`${adminBtnOutline} h-[38px] border-lms-line`}>
                      {t('Edit')}
                    </button>
                  </div>
                );
              })}
            </section>
          )}
        </div>
      </div>

      {editing && (
        <Modal wide title={t(PAGE_LABELS[editing])} onClose={() => setEditing(null)}>
          <div className="space-y-4">
            <Field label={t('Title')}>
              <input
                value={cfg.pages?.[editing]?.title || ''}
                onChange={(e) => setPage(editing, { title: e.target.value })}
                placeholder={t(PAGE_LABELS[editing])}
                className={inputCls}
              />
            </Field>
            <Field label={t('Content')} hint={t('Leave empty to hide the page from the footer.')}>
              <RichText value={cfg.pages?.[editing]?.html || ''} onChange={(html) => setPage(editing, { html })} />
            </Field>
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setEditing(null)} className={adminBtnOutline}>{t('Done')}</button>
              <button type="button" onClick={async () => { await save(); setEditing(null); }} disabled={saving || !dirty} className={adminBtnPrimary}>
                {saving ? t('Saving…') : t('Save changes')}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
